import {
    ArcElement, BarController, BarElement, CategoryScale, Chart as ChartJS,
    Legend, LinearScale, LineController, LineElement, PointElement, TimeScale, Tooltip,
    type ChartData,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Chart, Pie } from 'react-chartjs-2';
import type { ComputeDistributionView } from './dashboardTypes';
import { formatFullNumber, formatReadableNumber } from './numberFormat';

ChartJS.register(
    ArcElement, BarController, BarElement, CategoryScale, Legend, LinearScale,
    LineController, LineElement, PointElement, TimeScale, Tooltip,
);

const COLORS = ['#0f766e', '#2563eb', '#d97706', '#9333ea', '#dc2626', '#0891b2', '#65a30d', '#db2777'];

export function ConfigDashboardVisuals({
    visuals,
    onSelectCategory,
}: {
    visuals: ComputeDistributionView[];
    onSelectCategory?: (filterId: string, category: string) => void;
}) {
    return (
        <>
            {visuals.map((visual, index) => (
                <section className="compute-panel" key={`${visual.type}-${index}`}>
                    <div className="compute-panel-heading">
                        <div>
                            <h2>{visual.title}</h2>
                            <span>{visual.type === 'pie' || visual.type === 'distributionTable'
                                ? 'Sum over selected dates'
                                : visual.type === 'percentStackedColumn' ? `Daily share of ${visual.valueLabel}`
                                    : `Daily ${visual.valueLabel}${visual.type === 'stackedColumnLine' ? ' and total' : ''}`}</span>
                        </div>
                    </div>
                    {!visual.slices.length ? (
                        <div className="compute-empty">No data is available for these filters.</div>
                    ) : visual.type === 'pie' ? (
                        <DistributionPie visual={visual} onSelectCategory={visual.filterId && onSelectCategory
                            ? category => onSelectCategory(visual.filterId!, category) : undefined} />
                    ) : visual.type === 'distributionTable' ? (
                        <DistributionTable visual={visual} onSelectCategory={visual.filterId && onSelectCategory
                            ? category => onSelectCategory(visual.filterId!, category) : undefined} />
                    ) : (
                        <DistributionTrend visual={visual} onSelectCategory={visual.filterId && onSelectCategory
                            ? category => onSelectCategory(visual.filterId!, category) : undefined} />
                    )}
                </section>
            ))}
        </>
    );
}

function DistributionPie({
    visual,
    onSelectCategory,
}: {
    visual: ComputeDistributionView;
    onSelectCategory?: (category: string) => void;
}) {
    const total = visual.slices.reduce((sum, slice) => sum + slice.value, 0);
    const colors = categoryColors(visual);
    const formatShare = (value: number) => total === 0 ? '0.00%' : `${(100 * value / total).toFixed(2)}%`;
    return (
        <div className="compute-distribution-split">
            {total > 0 ? (
                <div className="compute-chart">
                    <Pie
                        aria-label={`${visual.title}: share by ${visual.categoryLabel}`}
                        role="img"
                        data={{
                            labels: visual.slices.map(slice => slice.label),
                            datasets: [{
                                data: visual.slices.map(slice => slice.value),
                                backgroundColor: visual.slices.map(slice => colors.get(slice.label)!),
                            }],
                        }}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                legend: { display: false },
                                tooltip: {
                                    callbacks: {
                                        label: context => `${context.label}: ${formatFullNumber(context.parsed)} (${formatShare(context.parsed)})`,
                                    },
                                },
                            },
                            onClick: (_event, elements) => {
                                const slice = visual.slices[elements[0]?.index];
                                if (slice && !slice.isOthers) onSelectCategory?.(slice.filterValue ?? slice.label);
                            },
                        }}
                    />
                </div>
            ) : <div className="compute-empty">No positive values to display in the pie chart.</div>}
            <DistributionTable visual={visual} onSelectCategory={onSelectCategory} />
        </div>
    );
}

function DistributionTable({
    visual,
    onSelectCategory,
}: {
    visual: ComputeDistributionView;
    onSelectCategory?: (category: string) => void;
}) {
    const total = visual.slices.reduce((sum, slice) => sum + slice.value, 0);
    const colors = categoryColors(visual);
    const formatShare = (value: number) => total === 0 ? '\u2014' : `${(100 * value / total).toFixed(2)}%`;
    const formatValue = (value: number) => (
        <span title={formatFullNumber(value)}>{formatReadableNumber(value)}</span>
    );
    return (
        <div className="compute-table-wrap">
            <table className="compute-table">
                <caption>Distribution values and share</caption>
                <thead><tr><th>{visual.categoryLabel}</th><th>{visual.valueLabel}</th><th>Share</th></tr></thead>
                <tbody>
                    {visual.slices.map(slice => (
                        <tr key={slice.label}>
                            <td>
                                <span className="compute-series-swatch" style={{
                                    backgroundColor: colors.get(slice.label),
                                }} />
                                {onSelectCategory && !slice.isOthers ? (
                                    <button type="button" className="compute-category-link" onClick={() => onSelectCategory(slice.filterValue ?? slice.label)}>
                                        {slice.label}
                                    </button>
                                ) : slice.label}
                            </td>
                            <td>{formatValue(slice.value)}</td>
                            <td>{formatShare(slice.value)}</td>
                        </tr>
                    ))}
                </tbody>
                {visual.type === 'distributionTable' && (
                    <tfoot><tr><th>Total</th><td>{formatValue(total)}</td><td>{formatShare(total)}</td></tr></tfoot>
                )}
            </table>
        </div>
    );
}

function categoryColors(visual: ComputeDistributionView): Map<string, string> {
    return new Map([...visual.slices].sort((a, b) => a.label.localeCompare(b.label))
        .map((slice, index) => [slice.label, slice.isOthers ? '#7c8793' : COLORS[index % COLORS.length]]));
}

function DistributionTrend({
    visual,
    onSelectCategory,
}: {
    visual: ComputeDistributionView;
    onSelectCategory?: (category: string) => void;
}) {
    const showTotal = visual.type === 'stackedColumnLine';
    const percent = visual.type === 'percentStackedColumn';
    const colors = categoryColors(visual);
    const data: ChartData<'bar' | 'line', { x: string; y: number | null }[]> = {
        datasets: [
            ...visual.series.map(series => ({
                type: 'bar' as const,
                label: series.label,
                data: series.points,
                backgroundColor: colors.get(series.label),
                stack: 'categories',
                order: 1,
            })),
            ...(showTotal ? [{
                type: 'line' as const,
                label: 'Total',
                data: visual.dates.map((date, index) => ({ x: date, y: visual.totals[index] })),
                borderColor: '#172b4d',
                backgroundColor: '#172b4d',
                stack: 'total',
                order: 0,
                pointRadius: 1.5,
                borderWidth: 2,
            }] : []),
        ],
    };
    return (
        <div className="compute-chart">
            <Chart
                type="bar"
                aria-label={`${visual.title}: daily ${percent ? 'percentage of ' : ''}${visual.valueLabel} by ${visual.categoryLabel}${showTotal ? ' and total' : ''}`}
                role="img"
                data={data}
                options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    // Sparse datasets have different array indices for the same date.
                    interaction: { mode: 'nearest', axis: 'x', intersect: false },
                    scales: {
                        x: { type: 'time', stacked: true, time: { tooltipFormat: 'PP' }, grid: { display: false } },
                        y: {
                            stacked: true, beginAtZero: true, max: percent ? 100 : undefined,
                            ticks: { callback: value => percent ? `${value}%` : formatReadableNumber(Number(value)) },
                        },
                    },
                    plugins: {
                        legend: {
                            position: 'bottom',
                            maxHeight: 90,
                            onClick: (_event, item) => {
                                const series = visual.series[item.datasetIndex ?? -1];
                                if (series && !series.isOthers) onSelectCategory?.(series.filterValue ?? series.label);
                            },
                        },
                        tooltip: {
                            callbacks: {
                                label: context => `${context.dataset.label}: ${percent
                                    ? `${context.parsed.y.toFixed(2)}%` : formatFullNumber(context.parsed.y)}`,
                            },
                        },
                    },
                }}
            />
        </div>
    );
}
